/**
 * Per-tenant crew display overrides (color + name) and schedule UI prefs.
 * Tenant-scoped localStorage, no backend round-trip. Lift to a tenants
 * column later if multi-device sync becomes a need.
 */

const STORAGE_KEY = 'crewsync.crew_display.v1';
const PREFS_KEY = 'crewsync.schedule_prefs.v1';

export interface CrewOverride {
  name?: string;
  color?: string;
}

type Store = Record<string, Record<string, CrewOverride>>; // tenantId -> crewKey -> override

function loadStore(): Store {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return (parsed && typeof parsed === 'object') ? parsed as Store : {};
  } catch {
    return {};
  }
}

function saveStore(store: Store): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
    window.dispatchEvent(new CustomEvent('crewsync:crew-display-changed'));
  } catch {
    // localStorage full or disabled — silent fail, in-memory continues
  }
}

export function getCrewOverrides(tenantId: string | null): Record<string, CrewOverride> {
  if (!tenantId) return {};
  const store = loadStore();
  return store[tenantId] || {};
}

export function setCrewOverride(tenantId: string | null, crewKey: string, patch: CrewOverride): void {
  if (!tenantId || !crewKey) return;
  const store = loadStore();
  const tenantSlot = { ...(store[tenantId] || {}) };
  const prev = tenantSlot[crewKey] || {};
  const next: CrewOverride = { ...prev, ...patch };
  // Drop empty keys so we don't accumulate junk.
  if (next.name !== undefined && next.name.trim() === '') delete next.name;
  if (next.color !== undefined && next.color.trim() === '') delete next.color;
  if (Object.keys(next).length === 0) {
    delete tenantSlot[crewKey];
  } else {
    tenantSlot[crewKey] = next;
  }
  store[tenantId] = tenantSlot;
  saveStore(store);
}

export function clearCrewOverride(tenantId: string | null, crewKey: string): void {
  if (!tenantId || !crewKey) return;
  const store = loadStore();
  if (!store[tenantId]) return;
  const tenantSlot = { ...store[tenantId] };
  delete tenantSlot[crewKey];
  if (Object.keys(tenantSlot).length === 0) delete store[tenantId];
  else store[tenantId] = tenantSlot;
  saveStore(store);
}

/** Returns the operator-visible label for a crew (override.name || crewKey). */
export function getCrewLabel(overrides: Record<string, CrewOverride>, crewKey: string): string {
  return (overrides[crewKey]?.name?.trim()) || crewKey;
}

/** Returns the resolved color for a crew (override.color || palette fallback). */
export function getCrewColor(
  overrides: Record<string, CrewOverride>,
  paletteFallback: Record<string, string>,
  crewKey: string,
  paletteDefault = '#00C2A8',
): string {
  return overrides[crewKey]?.color || paletteFallback[crewKey] || paletteDefault;
}

// ─── Schedule prefs (view + names) ────────────────────────────────────────

export type ScheduleView = 'day' | 'week' | 'month';

export interface SchedulePrefs {
  view: ScheduleView;
  showNames: boolean;
}

const DEFAULT_PREFS: SchedulePrefs = { view: 'month', showNames: true };

type PrefsStore = Record<string, SchedulePrefs>;

function loadPrefsStore(): PrefsStore {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(PREFS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return (parsed && typeof parsed === 'object') ? parsed as PrefsStore : {};
  } catch {
    return {};
  }
}

function savePrefsStore(store: PrefsStore): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(PREFS_KEY, JSON.stringify(store));
  } catch {
    // localStorage full or disabled — silent fail
  }
}

/** Returns persisted schedule prefs for a tenant, or sensible defaults. */
export function getSchedulePrefs(tenantId: string | null): SchedulePrefs {
  if (!tenantId) return DEFAULT_PREFS;
  const store = loadPrefsStore();
  const found = store[tenantId];
  if (!found) return DEFAULT_PREFS;
  return {
    view: (found.view === 'day' || found.view === 'week' || found.view === 'month') ? found.view : DEFAULT_PREFS.view,
    showNames: typeof found.showNames === 'boolean' ? found.showNames : DEFAULT_PREFS.showNames,
  };
}

/** Patches the persisted prefs (partial update). */
export function setSchedulePrefs(tenantId: string | null, patch: Partial<SchedulePrefs>): void {
  if (!tenantId) return;
  const store = loadPrefsStore();
  const prev = store[tenantId] || DEFAULT_PREFS;
  store[tenantId] = { ...prev, ...patch };
  savePrefsStore(store);
}
